import React, { useState, useRef, useEffect } from 'react';

const ETH_MONTHS = ['Meskerem', 'Tikimt', 'Hidar', 'Tahsas', 'Ter', 'Yekatit', 'Magabit', 'Miazia', 'Ginbot', 'Sene', 'Hamle', 'Nehase', 'Pagume'];

function getDaysInMonth(year, month) {
  if (month < 12) return 30;
  return year % 4 === 3 ? 6 : 5;
}

function parseEthDate(str) {
  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  let [, d, month, y] = m;
  d = parseInt(d); month = parseInt(month); y = parseInt(y);
  if (month < 1 || month > 13) return null;
  if (d < 1 || d > getDaysInMonth(y, month - 1)) return null;
  return { day: d, month, year: y };
}

function ethToJdn(year, month, day) {
  const epoch = 1724221;
  return epoch + (year - 1) * 365 + Math.floor(year / 4) + (month - 1) * 30 + (day - 1);
}

function EthiopianDatePicker({ value, onChange, placeholder, className, required, name }) {
  const [open, setOpen] = useState(false);
  const [navYear, setNavYear] = useState(2010);
  const [navMonth, setNavMonth] = useState(0);
  const ref = useRef(null);

  const parsed = parseEthDate(value);
  useEffect(() => {
    if (parsed) {
      setNavYear(parsed.year);
      setNavMonth(parsed.month - 1);
    } else {
      setNavYear(2010);
      setNavMonth(0);
    }
  }, [open]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const days = getDaysInMonth(navYear, navMonth);
  const jdnFirst = ethToJdn(navYear, navMonth + 1, 1);
  const firstDayOfWeek = (jdnFirst + 1) % 7;

  const today = toEthStr(new Date());

  function handleSelect(d) {
    const sel = `${String(d).padStart(2, '0')}/${String(navMonth + 1).padStart(2, '0')}/${String(navYear).padStart(4, '0')}`;
    onChange({ target: { name, value: sel } });
    setOpen(false);
  }

  function handleTextChange(e) {
    onChange({ target: { name, value: e.target.value } });
  }

  function prevYear() { setNavYear(y => y - 1); }
  function nextYear() { setNavYear(y => y + 1); }
  function prevMonth() { if (navMonth === 0) { setNavMonth(12); setNavYear(y => y - 1); } else setNavMonth(m => m - 1); }
  function nextMonth() { if (navMonth === 12) { setNavMonth(0); setNavYear(y => y + 1); } else setNavMonth(m => m + 1); }

  return (
    <div className="relative" ref={ref}>
      <input
        type="text"
        value={value}
        name={name}
        onChange={handleTextChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || 'dd/mm/yyyy'}
        className={className}
        required={required}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 bg-white border rounded shadow-lg p-3 w-72" style={{ minWidth: '280px' }}>
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevYear} className="px-2 py-1 hover:bg-gray-100 rounded text-sm">&lt;&lt;</button>
            <button type="button" onClick={prevMonth} className="px-2 py-1 hover:bg-gray-100 rounded text-sm">&lt;</button>
            <span className="font-bold text-sm">{ETH_MONTHS[navMonth]} {navYear}</span>
            <button type="button" onClick={nextMonth} className="px-2 py-1 hover:bg-gray-100 rounded text-sm">&gt;</button>
            <button type="button" onClick={nextYear} className="px-2 py-1 hover:bg-gray-100 rounded text-sm">&gt;&gt;</button>
          </div>
          <div className="grid grid-cols-7 gap-0 text-center text-xs font-medium text-gray-500 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="py-1">{d[0]}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0 text-center text-sm">
            {Array.from({ length: firstDayOfWeek }, (_, i) => (
              <div key={`empty-${i}`} className="py-1" />
            ))}
            {Array.from({ length: days }, (_, i) => {
              const d = i + 1;
              const selStr = `${String(d).padStart(2, '0')}/${String(navMonth + 1).padStart(2, '0')}/${String(navYear).padStart(4, '0')}`;
              const isSelected = selStr === value;
              const isToday = selStr === today;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => handleSelect(d)}
                  className={`py-1 rounded hover:bg-[#006400] hover:text-white ${isSelected ? 'bg-[#006400] text-white' : ''} ${isToday && !isSelected ? 'ring-1 ring-[#006400]' : ''}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <button type="button" onClick={() => { onChange({ target: { name, value: today } }); setOpen(false); }} className="text-[#006400] hover:underline">Today</button>
            <button type="button" onClick={() => setOpen(false)} className="text-gray-500 hover:underline">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function toEthStr(date) {
  const gy = date.getFullYear(), gm = date.getMonth() + 1, gd = date.getDate();
  const j = jdn(gy, gm, gd);
  const epoch = 1724221;
  const d = j - epoch;
  const cyc = Math.floor(d / 1461);
  let rem = d % 1461;
  let y, doy;
  if (rem < 365) { y = cyc * 4 + 1; doy = rem + 1; }
  else if (rem < 730) { y = cyc * 4 + 2; doy = rem - 365 + 1; }
  else if (rem < 1095) { y = cyc * 4 + 3; doy = rem - 730 + 1; }
  else { y = cyc * 4 + 4; doy = rem - 1095 + 1; }
  let m, day;
  if (doy <= 360) { m = Math.ceil(doy / 30); day = doy - (m - 1) * 30; }
  else { m = 13; day = doy - 360; }
  return `${String(day).padStart(2, '0')}/${String(m).padStart(2, '0')}/${String(y).padStart(4, '0')}`;
}

function jdn(y, m, d) {
  const a = Math.floor((14 - m) / 12);
  return d + Math.floor((153 * (m + 12 * a - 3) + 5) / 5) + 365 * (y + 4800 - a) + Math.floor((y + 4800 - a) / 4) - Math.floor((y + 4800 - a) / 100) + Math.floor((y + 4800 - a) / 400) - 32045;
}

export default EthiopianDatePicker;
